import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Location } from '../../models/Location';
import { MatDialog } from '@angular/material';

import { MapifyMap } from '../../models/MapifyMap';
import { RequestPreviewDialogComponent } from '../request-preview-dialog/request-preview-dialog.component';
import { AccordionComponent } from '../accordion/accordion.component';
import { AccordionItemComponent } from '../accordion/accordion-item.component';
import { environment } from '../../../environments/environment';
import { Finish } from '../../models/Finish';
import { Print } from '../../models/Print';
import { Coordinate } from '../../models/Coordinate';
import { MapifyPricePipe } from '../../pipes/mapify-price.pipe';
import { UtilsService } from '../../services/utils.service';
import { SettingsService } from 'app/services/settings.service';
import { MapService } from 'app/services/map.service';
import { Frame } from 'app/models/Frame';

declare global {
  interface Window {
    Snipcart: any;
  }
}

@Component({
  selector: 'mpf-map-configurator',
  templateUrl: './map-configurator.component.html',
  styleUrls: ['./map-configurator.component.scss']
})
export class MapConfiguratorComponent implements OnInit {
  @Input() map: MapifyMap;
  @ViewChild('accordion') accordion: AccordionComponent;
  @ViewChild('locationAccordionItem') locationAccordionItem: AccordionItemComponent;

  print: Print;
  frame: Frame | undefined;
  smartphoneDialogSize = {
    height: '100%',
    width: '100%',
    maxWidth: '100%'
  };

  constructor(
    public dialog: MatDialog,
    private decimalPipe: DecimalPipe,
    private mapifyPricePipe: MapifyPricePipe,
    private utils: UtilsService,
    private settingsService: SettingsService,
    private mapService: MapService
  ) {}

  ngOnInit() {
    // TODO: use observables and debounce resize events, maybe we could create a service
    // to handle window events
    window.addEventListener('resize', this.onResize.bind(this));

    this.setAccordionMode();
    this.locationAccordionItem.isOpen = !this.isMobile();
  }

  setAccordionMode() {
    // if mobile, then don't let the accordion show multiple items opened at the same time
    this.accordion.mode = this.isMobile() ? 'single' : 'multiple';
  }

  setLocation(location: Location) {
    const coordinateToDescription = (coordinate: Coordinate): string => {
      let description;
      const { lat, lng } = coordinate;
      const format = '1.3-3';

      if (lat < 0) {
        description = this.decimalPipe.transform(Math.abs(lat), format) + '°S / ';
      } else {
        description = this.decimalPipe.transform(lat, format) + '°N / ';
      }

      if (lng < 0) {
        description += this.decimalPipe.transform(Math.abs(lng), format) + '°W';
      } else {
        description += this.decimalPipe.transform(lng, format) + '°E';
      }

      return description;
    };

    const { city, country, center } = location;
    this.map.center = center;
    const isRomantic = this.utils.isRomanticEnabled(this.map);

    if (this.map.texts.title) {
      this.map.texts.title.value.next(isRomantic ? this.utils.toProperCase(city) : city.toUpperCase());
    }

    if (this.map.texts.subtitle) {
      this.map.texts.subtitle.value.next(isRomantic ? this.utils.toProperCase(country) : country.toUpperCase());
    }

    if (this.map.texts.description) {
      this.map.texts.description.value.next(coordinateToDescription(center));
    }

    if (this.isMobile()) {
      this.accordion.closeAll();
    }
  }

  setPrint(print: Print) {
    // force print update when orientation changes
    this.print = { ...print };
    this.frame = undefined;
    this.map.printId = this.print.id;
  }

  setFrame(frameId: string) {
    this.frame = this.print.frames.find(f => f.id === frameId);
  }

  getPrintPrice(): string {
    const productIds = [this.print.id];

    if (this.frame) {
      productIds.push(this.frame.id);
    }

    return this.mapifyPricePipe.transform(productIds);
  }

  isMobile() {
    return window.innerWidth <= environment.screen.tablet;
  }

  // TODO: find what event type should we use for window events, Event does not work!
  onResize() {
    this.setAccordionMode();
  }

  openRequestPreviewDialog() {
    const dialogRef = this.dialog.open(RequestPreviewDialogComponent, this.getRequestPreviewDialogConfig());

    dialogRef.componentInstance.previewRequest = {
      map: this.map,
      print: this.print,
      name: '',
      email: '',
      message: '',
      wantsNewsletter: false
    };

    const updateDialogSize = () => {
      const requestPreviewDialogConfig = this.getRequestPreviewDialogConfig();
      dialogRef.updateSize(requestPreviewDialogConfig.width, requestPreviewDialogConfig.height);
      dialogRef.updatePosition();
    };

    window.addEventListener('resize', updateDialogSize);

    dialogRef.afterClosed().subscribe(() => {
      window.removeEventListener('resize', updateDialogSize);
    });
  }

  getRequestPreviewDialogConfig() {
    const windowWidth = window.innerWidth;

    if (windowWidth < environment.screen.smartphone) {
      return this.smartphoneDialogSize;
    } else {
      return {
        height: '580px',
        width: '475px'
      };
    }
  }

  setFinish(finish: Finish) {
    this.map.finish = finish;
  }

  checkout() {
    const lang = this.settingsService.getLanguage();
    const currency = this.settingsService.getCurrency();
    // Set the snipcart currency/langauge to what is currently selected
    window.Snipcart.api.session.setCurrency(currency.toLowerCase());
    window.Snipcart.api.session.setLanguage(lang);

    // Get data from the map
    try {
      const mapString = localStorage.getItem('map');

      if (mapString) {
        const map = JSON.parse(mapString);

        const { texts, size } = map;

        // Set the data-items
        const button = document.querySelector('#add-to-snip-basket')!;

        const itemId = this.frame ? this.frame.id : map.printId;
        // id
        button.setAttribute('data-item-id', itemId);

        const defaultTitle = `CUSTOM ${size.width} x ${size.height}${size.unit}`;
        // title, description
        const title = texts.title && texts.title.value !== '' ? texts.title.value : defaultTitle;

        let description =
          texts.subtitle && texts.subtitle.value !== '' && texts.description && texts.description.value !== ''
            ? texts.subtitle.value + '\n' + texts.description.value + '\n' + size.width + 'x' + size.height + size.unit
            : '';

        button.setAttribute('data-item-name', title);
        button.setAttribute('data-item-description', description);

        if (this.frame) {
          const frameText = `${lang === 'de' ? 'mit' : 'with'} ${this.frame.name[this.settingsService.getLanguage()]}`;

          button.setAttribute('data-item-custom1-name', '');
          button.setAttribute('data-item-custom1-type', 'readonly');
          button.setAttribute('data-item-custom1-value', frameText);
        } else {
          button.removeAttribute('data-item-custom1-name');
          button.removeAttribute('data-item-custom1-type');
          button.removeAttribute('data-item-custom1-value');
        }

        let price = environment.mapify.prices[currency][map.printId];
        if (this.frame) {
          const framePrice = environment.mapify.prices[currency][this.frame.id];
          price += framePrice;
        }

        if (price) {
          button.setAttribute('data-item-price', price.toString());
        } else {
          throw new Error('There is not price defined!');
        }

        // Image
        button.setAttribute('data-item-image', this.mapService.getThumbnailUrl(mapString));

        // meta data map code
        button.setAttribute('data-item-metadata', JSON.stringify({ map_code: mapString }));
      }
    } catch (err) {
      console.error(err);
    }
  }
}



// WEBPACK FOOTER //
// /vercel/path0/src/app/components/map-configurator/map-configurator.component.ts